import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobArchitectureGenerationInfo } from '~/shared/data-contract/job-architecture';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobProfileComponent, JobProfileLegacy } from '~/shared/data-contract/job-profile';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import type { JobProfileUpdatePayload } from '../../data-contract/job-profile-update';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import type { JobItemDetails } from './use-job-item-details';

type UpdateJobProfileVariables = {
    readonly acceptLanguage: string;
    readonly architectureId: string;
    readonly jobItemType:
        | { readonly id: string; readonly type: 'job-level' }
        | { readonly id: string; readonly type: 'job-role' };
    readonly jobProfile: JobProfileLegacy;
    readonly tasks?: JobProfileUpdatePayload['tasks'];
    readonly tasksBaseVersion?: number;
    readonly version?: string;
    readonly versionComment?: string;
    readonly withAutoTranslation: boolean;
};

export type UseUpdateJobProfileResult = UseMutationResult<JobProfileLegacy, Error, UpdateJobProfileVariables>;

const mergeTranslations = (
    existingTranslations: JobProfileLegacy['translations'],
    newTranslations: JobProfileLegacy['translations'],
): JobProfileLegacy['translations'] => {
    const translationsMap = new Map(existingTranslations.map((translation) => [translation.language, translation]));
    for (const translation of newTranslations) {
        translationsMap.set(translation.language, translation);
    }

    return [...translationsMap.values()];
};

const mergeJobProfiles = (
    existingProfile: JobProfileLegacy,
    updatedProfile: JobProfileComponent | undefined,
): JobProfileLegacy => {
    if (is.undefined(updatedProfile)) {
        return existingProfile;
    }

    return {
        ...existingProfile,
        ...updatedProfile,
        translations: mergeTranslations(existingProfile.translations, updatedProfile.translations),
    };
};

const getDetailQueryKey = ({
    architectureId,
    jobItemType,
    version,
}: Pick<UpdateJobProfileVariables, 'architectureId' | 'jobItemType' | 'version'>) =>
    jobItemType.type === 'job-level'
        ? jobArchitectureKeys.level(architectureId, jobItemType.id, version)
        : jobArchitectureKeys.role(architectureId, jobItemType.id, version);

const getOptimisticGenerationInfo = (
    generationInfo: JobArchitectureGenerationInfo | undefined,
    withAutoTranslation: boolean,
): JobArchitectureGenerationInfo | undefined => {
    if (!withAutoTranslation) {
        return generationInfo;
    }

    return {
        ...(generationInfo ?? { inProgress: false }),
        translationState: 'queued',
    };
};

const reconcileRole = (current: JobRoleDetails, updatedRole: JobRole): JobRoleDetails => ({
    ...current,
    actions: updatedRole.actions,
    childCount: updatedRole.childCount,
    creator: updatedRole.creator,
    isArchived: updatedRole.isArchived,
    jobProfile: is.undefined(current.jobProfile)
        ? undefined
        : mergeJobProfiles(current.jobProfile, updatedRole.jobProfile),
    parentId: updatedRole.parentId ?? current.parentId,
    profileGenerationInfo: updatedRole.profileGenerationInfo ?? current.profileGenerationInfo,
    reviewer: updatedRole.reviewer,
    statistics: updatedRole.statistics,
    title: updatedRole.title,
    type: 'job-role',
    versionInfo: updatedRole.versionInfo,
});

const reconcileLevel = (current: JobLevelDetails, updatedLevel: JobLevel): JobLevelDetails => ({
    ...current,
    actions: updatedLevel.actions,
    creator: updatedLevel.creator,
    isArchived: updatedLevel.isArchived,
    jobProfile: is.undefined(current.jobProfile)
        ? undefined
        : mergeJobProfiles(current.jobProfile, updatedLevel.jobProfile),
    parentId: updatedLevel.parentId ?? current.parentId,
    profileGenerationInfo: updatedLevel.profileGenerationInfo ?? current.profileGenerationInfo,
    reviewer: updatedLevel.reviewer,
    title: updatedLevel.title,
    type: 'job-level',
    versionInfo: updatedLevel.versionInfo,
});

const reconcileJobItem = (
    current: JobItemDetails | undefined,
    updatedItem: JobLevel | JobRole,
): JobItemDetails | undefined => {
    if (is.undefined(current)) {
        return current;
    }

    if (current.type === 'job-role' && updatedItem.type === 'job-role') {
        return reconcileRole(current, updatedItem);
    }

    if (current.type === 'job-level' && updatedItem.type === 'job-level') {
        return reconcileLevel(current, updatedItem);
    }

    return current;
};

type MutationContext = {
    readonly previousData: JobItemDetails | undefined;
    readonly profileApi: 'job-item' | 'legacy-profile';
    readonly queryKey: ReturnType<typeof getDetailQueryKey>;
};

export const useUpdateJobProfile = (): UseUpdateJobProfileResult => {
    const { updateJobItemProfile, updateJobProfile } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const profileApi = featureFlagService.isFeatureFlagEnabled('new-job-profile-api') ? 'job-item' : 'legacy-profile';

    return useMutation<JobProfileLegacy, Error, UpdateJobProfileVariables, MutationContext>({
        mutationFn: async ({
            acceptLanguage,
            architectureId,
            jobItemType,
            jobProfile,
            tasks,
            tasksBaseVersion,
            version,
            versionComment,
            withAutoTranslation,
        }) => {
            if (profileApi === 'legacy-profile') {
                const legacyResult = await updateJobProfile({
                    acceptLanguage,
                    jobProfile,
                    versionComment,
                    withAutoTranslation,
                });

                if (legacyResult.isErr) {
                    throw legacyResult.error;
                }

                return legacyResult.value;
            }

            const result = await updateJobItemProfile({
                acceptLanguage,
                architectureId,
                jobItemType,
                jobProfile,
                tasks,
                tasksBaseVersion,
                versionComment,
                withAutoTranslation,
            });

            if (result.isErr) {
                throw result.error;
            }

            const detailQueryKey = getDetailQueryKey({ architectureId, jobItemType, version });
            queryClient.setQueryData<JobItemDetails>(detailQueryKey, (current) =>
                reconcileJobItem(current, result.value),
            );

            return mergeJobProfiles(jobProfile, result.value.jobProfile);
        },
        onError: (_error, _variables, context) => {
            if (context?.profileApi === 'legacy-profile') {
                return;
            }
            if (!is.undefined(context?.previousData)) {
                queryClient.setQueryData(context.queryKey, context.previousData);
            }
        },
        onMutate: async ({ architectureId, jobItemType, jobProfile, version, withAutoTranslation }) => {
            const queryKey = getDetailQueryKey({ architectureId, jobItemType, version });
            const usesNewApi = profileApi === 'job-item';

            await queryClient.cancelQueries(usesNewApi ? { exact: true, queryKey } : { queryKey });
            const previousData = usesNewApi ? queryClient.getQueryData<JobItemDetails>(queryKey) : undefined;

            queryClient.setQueryData<JobItemDetails>(queryKey, (current) => {
                if (is.undefined(current?.jobProfile)) {
                    return current;
                }

                return {
                    ...current,
                    jobProfile: {
                        ...current.jobProfile,
                        ...jobProfile,
                        translations: mergeTranslations(current.jobProfile.translations, jobProfile.translations),
                    },
                    profileGenerationInfo: usesNewApi
                        ? getOptimisticGenerationInfo(current.profileGenerationInfo, withAutoTranslation)
                        : current.profileGenerationInfo,
                };
            });

            return { previousData, profileApi, queryKey };
        },
        onSuccess: async (_updatedProfile, { architectureId, jobItemType }, context) => {
            if (context.profileApi === 'legacy-profile') {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: jobArchitectureKeys.role(architectureId, jobItemType.id),
                    }),
                    queryClient.invalidateQueries({
                        queryKey: jobArchitectureKeys.level(architectureId, jobItemType.id),
                    }),
                    queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.levels(architectureId) }),
                    queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.roles(architectureId) }),
                ]);
                return;
            }

            const versionQueryKey =
                jobItemType.type === 'job-role'
                    ? jobArchitectureKeys.roleVersions(architectureId, jobItemType.id)
                    : jobArchitectureKeys.levelVersions(architectureId, jobItemType.id);

            await Promise.all([
                queryClient.invalidateQueries({ exact: true, queryKey: context.queryKey }),
                queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.levels(architectureId) }),
                queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.roles(architectureId) }),
                queryClient.invalidateQueries({ queryKey: versionQueryKey }),
                queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.history(architectureId) }),
            ]);
        },
    });
};
